import React, { useState, useEffect } from 'react';
import { Truck, Package, MapPin, Clock, User, CheckCircle, AlertTriangle, Eye, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const WarehouseIncomingDeliveries = () => {
  const [incomingVehicles, setIncomingVehicles] = useState([]);
  const [vehiclesByProject, setVehiclesByProject] = useState({});
  const [vehicleItems, setVehicleItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [confirmingDelivery, setConfirmingDelivery] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Filtering states
  const [statusFilter, setStatusFilter] = useState('all');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Item confirmation state
  const [confirmedItems, setConfirmedItems] = useState({});
  // Keep track of confirmed items across different assignments for UI persistence
  const [confirmedItemsHistory, setConfirmedItemsHistory] = useState({});
  
  // Return items state
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returningVehicle, setReturningVehicle] = useState(null);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [returnItems, setReturnItems] = useState([]);
  
  // Collapsible project tables state
  const [expandedProjects, setExpandedProjects] = useState({});

  const statusColors = {
    'assigned_loading': 'bg-blue-100 text-blue-800 border-blue-200',
    'in_transit_to_project': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'at_project_site': 'bg-purple-100 text-purple-800 border-purple-200',
    'delivered': 'bg-green-100 text-green-800 border-green-200',
    'not_completed': 'bg-orange-100 text-orange-800 border-orange-200',
    'in_transit_to_warehouse': 'bg-blue-100 text-blue-800 border-blue-200'
  };

  const statusLabels = {
    'assigned_loading': 'Loading',
    'in_transit_to_project': 'To Project',
    'at_project_site': 'At Project',
    'delivered': 'Delivered',
    'not_completed': 'Not Completed',
    'in_transit_to_warehouse': 'Returning'
  };

  useEffect(() => {
    fetchIncomingVehicles();
  }, []);

  const fetchIncomingVehicles = async () => {
    try {
      setLoading(true);
      
      // Get all vehicle assignments
      const assignmentsResponse = await axios.get(`${API_BASE_URL}/api/vehicle-assignments/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      console.log('All assignments response:', assignmentsResponse.data);

      // Filter for active assignments (not returned) - including all loading and delivery statuses
      const activeAssignments = assignmentsResponse.data.filter(assignment => {
        console.log('Assignment:', {
          id: assignment.id,
          status: assignment.status,
          car_details: assignment.car_details,
          actual_return_date: assignment.actual_return_date
        });

        return !assignment.actual_return_date && (
          assignment.status === 'assigned' ||
          assignment.status === 'assigned_loading' ||
          assignment.status === 'in_transit_to_project' ||
          assignment.status === 'at_project_site' ||
          assignment.status === 'delivered' ||
          assignment.status === 'not_completed' ||
          assignment.status === 'in_transit_to_warehouse'
        );
      });

      console.log('Filtered active assignments:', activeAssignments);

      setIncomingVehicles(activeAssignments);

      // Fetch project details for each assignment
      const projectPromises = activeAssignments.map(async (assignment) => {
        if (assignment.project && !assignment.project_details?.name) {
          try {
            const projectResponse = await axios.get(`${API_BASE_URL}/api/projects/${assignment.project}/`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });
            return {
              ...assignment,
              project_details: projectResponse.data
            };
          } catch (error) {
            console.error(`Error fetching project ${assignment.project}:`, error);
            return assignment;
          }
        }
        return assignment;
      });

      const assignmentsWithProjects = await Promise.all(projectPromises);

      // Fetch vehicle details for each assignment to get car_status
      const vehiclePromises = assignmentsWithProjects.map(async (assignment) => {
        if (assignment.car && !assignment.car_details) {
          try {
            const vehicleResponse = await axios.get(`${API_BASE_URL}/api/cars/${assignment.car}/`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });
            assignment.car_details = vehicleResponse.data;
            console.log('Fetched vehicle details:', assignment.car_details);
          } catch (error) {
            console.error(`Error fetching vehicle ${assignment.car}:`, error);
          }
        }

        // If car_details doesn't have car_status, set it based on assignment status
        if (assignment.car_details && !assignment.car_details.car_status) {
          assignment.car_details.car_status = assignment.status;
        }

        return assignment;
      });

      const assignmentsWithVehicles = await Promise.all(vehiclePromises);
      console.log('Final assignments with vehicles:', assignmentsWithVehicles);

      // Group vehicles by project
      const groupedByProject = assignmentsWithVehicles.reduce((acc, assignment) => {
        const projectName = assignment.project_name || 
                           assignment.project_details?.name || 
                           `Project ${assignment.project}` || 
                           'Unknown Project';
        if (!acc[projectName]) {
          acc[projectName] = [];
        }
        acc[projectName].push(assignment);
        return acc;
      }, {});
      setVehiclesByProject(groupedByProject);

      // Items are already included in VehicleAssignmentSerializer as 'assigned_items'
      const itemsMap = {};
      assignmentsWithVehicles.forEach(assignment => {
        console.log(`Items for vehicle ${assignment.car}:`, {
          assigned_items: assignment.assigned_items,
          assignment_id: assignment.id,
          car_details: assignment.car_details
        });

        itemsMap[assignment.car] = assignment.assigned_items || [];

        // If no items, try to fetch them separately
        if (!assignment.assigned_items || assignment.assigned_items.length === 0) {
          console.log(`No assigned_items for assignment ${assignment.id}, will try to fetch separately`);
        }
      });

      console.log('Final itemsMap:', itemsMap);
      setVehicleItems(itemsMap);

      // Try to fetch items separately for assignments that don't have them
      const itemsFetchPromises = assignmentsWithVehicles.map(async (assignment) => {
        if (!assignment.assigned_items || assignment.assigned_items.length === 0) {
          try {
            console.log(`Fetching items for assignment ${assignment.id}`);
            const itemsResponse = await axios.get(`${API_BASE_URL}/api/vehicle-assignment-items/?assignment=${assignment.id}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });
            console.log(`Items response for assignment ${assignment.id}:`, itemsResponse.data);

            if (itemsResponse.data && itemsResponse.data.length > 0) {
              itemsMap[assignment.car] = itemsResponse.data;
            }
          } catch (error) {
            console.error(`Error fetching items for assignment ${assignment.id}:`, error);
          }
        }
      });

      await Promise.all(itemsFetchPromises);
      console.log('Updated itemsMap after separate fetch:', itemsMap);
      setVehicleItems({...itemsMap}); // Force re-render

    } catch (error) {
      console.error('Error fetching incoming vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const openItemsModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowItemsModal(true);
  };

  const updateInventoryStock = async (items, confirmedItems) => {
    try {
      console.log('Updating inventory stock for confirmed items...');
      
      // Process each confirmed item for inventory update
      for (let i = 0; i < items.length; i++) {
        if (confirmedItems[i]) {
          const item = items[i];
          const quantityToAdd = item.quantity_issued || item.quantity || 0;
          
          if (quantityToAdd > 0 && item.inventory_item_id) {
            try {
              console.log(`Adding ${quantityToAdd} units to inventory item ${item.inventory_item_id}`);
              
              // Use the stock adjustment endpoint to add inventory
              await axios.post(`${API_BASE_URL}/api/warehouse/items/${item.inventory_item_id}/adjust_stock/`, {
                adjustment: quantityToAdd,
                notes: `Stock replenishment from delivery - Vehicle: ${item.vehicle_info || 'Unknown'}, Issue: ${item.issue_number || 'N/A'}`
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              });

              // Create stock movement record
              await axios.post(`${API_BASE_URL}/api/warehouse/stock-movements/`, {
                item: item.inventory_item_id,
                movement_type: 'stock_in',
                quantity: quantityToAdd,
                reference_number: `DEL-${Date.now()}-${item.issue_number || 'N/A'}`,
                notes: `Delivery confirmation - returned to warehouse stock`
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              });

              console.log(`Successfully updated inventory for item ${item.item_name}: +${quantityToAdd} units`);
            } catch (error) {
              console.error(`Error updating inventory for item ${item.item_name}:`, error);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error in updateInventoryStock:', error);
    }
  };

  const openConfirmModal = (assignment) => {
    console.log('Opening confirm modal for assignment:', assignment);
    setConfirmingDelivery(assignment);
    
    // Initialize confirmed items state - all items start as unconfirmed
    const itemsForVehicle = vehicleItems[assignment.car] || [];
    console.log('Items for vehicle:', itemsForVehicle);
    
    const initialConfirmedState = {};
    itemsForVehicle.forEach((item, index) => {
      initialConfirmedState[index] = false;
    });
    
    console.log('Initial confirmed state:', initialConfirmedState);
    setConfirmedItems(initialConfirmedState);
    setShowConfirmModal(true);
  };

  const confirmDelivery = async (assignment) => {
    try {
      const confirmationStatus = getConfirmationStatus();
      const items = vehicleItems[assignment.car] || [];
      
      // Debug: Check item structure
      console.log('Assignment:', assignment);
      console.log('Items structure:', items);
      console.log('First item:', items[0]);
      console.log('Assignment keys:', Object.keys(assignment));
      console.log('Assignment car field:', assignment.car);
      console.log('Assignment car_details:', assignment.car_details);
      
      // Determine new vehicle status based on confirmation
      let newVehicleStatus;
      if (confirmationStatus === 'all') {
        newVehicleStatus = 'delivered';
      } else if (confirmationStatus === 'partial') {
        newVehicleStatus = 'not_completed';
      } else {
        // If nothing confirmed, keep current status or set to returning
        newVehicleStatus = 'in_transit_to_warehouse';
      }

      // Process inventory updates for confirmed items
      await updateInventoryStock(items, confirmedItems);

      // Update vehicle status and location - try multiple approaches
      const projectLocation = assignment.project_details?.name || assignment.project_name || 'Project Site';
      
      try {
        // First try updating the assignment
        await axios.patch(`${API_BASE_URL}/api/vehicle-assignments/${assignment.id}/`, {
          status: newVehicleStatus, // Try 'status' instead of 'car_status'
          car_status: newVehicleStatus, // Also try 'car_status'
          delivery_status: newVehicleStatus, // Try 'delivery_status'
          current_location: projectLocation, // Update location to project
          notes: `Delivery confirmation: ${confirmationStatus} - ${Object.values(confirmedItems).filter(Boolean).length}/${items.length} items confirmed`
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        console.log('Vehicle assignment updated successfully');
      } catch (error) {
        console.error('Error updating vehicle assignment:', error);
        
        // Try updating the car record directly
        try {
          await axios.patch(`${API_BASE_URL}/api/cars/${assignment.car}/`, {
            car_status: newVehicleStatus,
            status: newVehicleStatus,
            current_location: projectLocation
          }, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          console.log('Vehicle status updated via cars endpoint');
        } catch (altError) {
          console.error('Both vehicle update methods failed:', altError);
          
          // Try a third approach - warehouse specific endpoint
          try {
            await axios.post(`${API_BASE_URL}/api/warehouse/confirm-delivery/`, {
              assignment_id: assignment.id,
              car_id: assignment.car,
              status: newVehicleStatus,
              confirmed_items: Object.values(confirmedItems).filter(Boolean).length,
              total_items: items.length
            }, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });
            console.log('Vehicle status updated via warehouse endpoint');
          } catch (warehouseError) {
            console.error('All three vehicle update methods failed:', warehouseError);
            // Don't throw - let the rest of the function continue
          }
        }
      }

      // Update individual item statuses based on confirmation
      console.log('Attempting individual item updates...');
      
      // Create a summary of confirmed items for the assignment update
      const confirmedItemsList = [];
      for (let i = 0; i < items.length; i++) {
        if (confirmedItems[i]) {
          confirmedItemsList.push({
            index: i,
            item_name: items[i].item_name,
            issue_number: items[i].issue_number,
            confirmed: true
          });
        }
      }
      
      console.log('Confirmed items list:', confirmedItemsList);
      
      // Try to update the assignment with confirmed items info
      if (confirmedItemsList.length > 0) {
        try {
          await axios.patch(`${API_BASE_URL}/api/vehicle-assignments/${assignment.id}/`, {
            confirmed_items: confirmedItemsList,
            delivery_status: newVehicleStatus,
            confirmation_notes: `${confirmedItemsList.length}/${items.length} items confirmed by warehouse`
          }, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          console.log('Assignment updated with confirmed items');
        } catch (error) {
          console.error('Error updating assignment with confirmed items:', error);
        }
      }

      const confirmedCount = Object.values(confirmedItems).filter(Boolean).length;
      const totalItemsForMessage = items.length;
      
      // Save confirmed items to history for UI persistence
      const assignmentKey = `${assignment.id}_${assignment.car}`;
      setConfirmedItemsHistory(prev => ({
        ...prev,
        [assignmentKey]: { ...confirmedItems }
      }));
      
      let message = `Delivery processed! ${confirmedCount}/${totalItemsForMessage} items confirmed. Status: ${statusLabels[newVehicleStatus]}`;
      
      // Special case for zero quantity items
      const totalQuantity = items.reduce((sum, item) => sum + (item.quantity_issued || item.quantity || 0), 0);
      if (totalQuantity === 0 && items.length > 0) {
        message += `\n(Note: All items had 0 quantity)`;
      }
      
      alert(message);
      
      setShowConfirmModal(false);
      setConfirmingDelivery(null);
      setConfirmedItems({});
      fetchIncomingVehicles(); // Refresh data
    } catch (error) {
      console.error('Error confirming delivery:', error);
      alert('Error processing delivery. Please try again.');
    }
  };


  // Filter function
  const applyFilters = (vehicles) => {
    return vehicles.filter(vehicle => {
      // Status filter
      if (statusFilter !== 'all') {
        if (vehicle.car_details?.car_status !== statusFilter) {
          return false;
        }
      }

      // Vehicle filter (search by plate number or brand/model)
      if (vehicleFilter) {
        const vehicleSearchText = `${vehicle.car_details?.plate_number || ''} ${vehicle.car_details?.car_brand || ''} ${vehicle.car_details?.car_model || ''}`.toLowerCase();
        if (!vehicleSearchText.includes(vehicleFilter.toLowerCase())) {
          return false;
        }
      }

      // Project filter
      if (projectFilter) {
        const projectName = (vehicle.project_name || vehicle.project_details?.name || '').toLowerCase();
        if (!projectName.includes(projectFilter.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  };

  // Get unique vehicle statuses for filter options
  const getUniqueStatuses = () => {
    const statuses = new Set();
    incomingVehicles.forEach(vehicle => {
      if (vehicle.car_details?.car_status) {
        statuses.add(vehicle.car_details.car_status);
      }
    });
    return Array.from(statuses);
  };

  // Apply filters to create filtered vehiclesByProject
  const getFilteredVehiclesByProject = () => {
    const filteredVehicles = applyFilters(incomingVehicles);
    
    // Group filtered vehicles by project
    const grouped = filteredVehicles.reduce((acc, assignment) => {
      const projectName = assignment.project_name || 
                         assignment.project_details?.name || 
                         `Project ${assignment.project}` || 
                         'Unknown Project';
      if (!acc[projectName]) {
        acc[projectName] = [];
      }
      acc[projectName].push(assignment);
      return acc;
    }, {});
    
    return grouped;
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setVehicleFilter('');
    setProjectFilter('');
  };

  // Item confirmation functions
  const toggleItemConfirmation = (itemIndex) => {
    setConfirmedItems(prev => ({
      ...prev,
      [itemIndex]: !prev[itemIndex]
    }));
  };

  const toggleAllItems = (confirm) => {
    const newState = {};
    Object.keys(confirmedItems).forEach(key => {
      newState[key] = confirm;
    });
    setConfirmedItems(newState);
  };

  const getConfirmationStatus = () => {
    const totalItems = Object.keys(confirmedItems).length;
    const confirmedCount = Object.values(confirmedItems).filter(Boolean).length;
    
    if (confirmedCount === 0) return 'none';
    if (confirmedCount === totalItems) return 'all';
    return 'partial';
  };

  // Check if an item was previously confirmed
  const isItemPreviouslyConfirmed = (assignment, itemIndex) => {
    const assignmentKey = `${assignment.id}_${assignment.car}`;
    return confirmedItemsHistory[assignmentKey]?.[itemIndex] || false;
  };

  // Return items functions
  const openReturnModal = async (projectName, projectVehicles) => {
    console.log(`Opening return modal for project: ${projectName}`);
    setReturningVehicle(null); // Will be selected in modal
    
    // Get available vehicles for this specific project only
    const vehicles = projectVehicles.filter(assignment => 
      assignment.car_details?.car_status === 'delivered' || 
      assignment.car_details?.car_status === 'at_project_site'
    );
    setAvailableVehicles(vehicles);
    
    // Get and aggregate delivered items from vehicles in this specific project only
    const itemsMap = {};
    vehicles.forEach(assignment => {
      const items = vehicleItems[assignment.car] || [];
      items.forEach(item => {
        const itemKey = item.item_name || item.name;
        const itemQuantity = item.quantity_issued || item.quantity || 0;
        
        if (itemsMap[itemKey]) {
          // Add to existing item
          itemsMap[itemKey].total_quantity += itemQuantity;
          itemsMap[itemKey].source_vehicles.push({
            vehicle: assignment.car_details?.plate_number,
            assignment_id: assignment.id,
            car_id: assignment.car,
            quantity: itemQuantity
          });
        } else {
          // Create new aggregated item
          itemsMap[itemKey] = {
            ...item,
            total_quantity: itemQuantity,
            project_name: projectName,
            source_vehicles: [{
              vehicle: assignment.car_details?.plate_number,
              assignment_id: assignment.id,
              car_id: assignment.car,
              quantity: itemQuantity
            }]
          };
        }
      });
    });
    
    // Convert map to array
    const projectDeliveredItems = Object.values(itemsMap);
    
    console.log(`Available vehicles for ${projectName}:`, vehicles);
    console.log(`Delivered items for ${projectName}:`, projectDeliveredItems);
    setAvailableItems(projectDeliveredItems);
    setReturnItems([]);
    setShowReturnModal(true);
  };

  const addReturnItem = (item, quantity = 1) => {
    const existingIndex = returnItems.findIndex(ri => ri.item_name === item.item_name);
    const maxQuantity = item.total_quantity || item.quantity_issued || item.quantity || 1;
    
    if (existingIndex >= 0) {
      const updated = [...returnItems];
      updated[existingIndex].quantity = Math.min(updated[existingIndex].quantity + quantity, maxQuantity);
      setReturnItems(updated);
    } else {
      setReturnItems([...returnItems, {
        ...item,
        quantity: quantity,
        max_quantity: maxQuantity
      }]);
    }
  };

  const removeReturnItem = (index) => {
    setReturnItems(returnItems.filter((_, i) => i !== index));
  };

  const updateReturnItemQuantity = (index, quantity) => {
    const updated = [...returnItems];
    updated[index].quantity = Math.max(0, Math.min(quantity, updated[index].max_quantity));
    setReturnItems(updated);
  };

  // Create return assignment function
  const createReturnAssignment = async () => {
    if (!returningVehicle || returnItems.length === 0) {
      alert('Please select a vehicle and items to return');
      return;
    }

    try {
      console.log('Creating return assignment...');
      console.log('Return items:', returnItems);
      console.log('Return vehicle:', returningVehicle);

      // Update vehicle status to returning
      const updateData = {
        car_status: 'in_transit_to_warehouse',
        status: 'in_transit_to_warehouse',
        current_location: 'Returning to warehouse',
        notes: `Return assignment created - ${returnItems.length} item types, ${returnItems.reduce((sum, item) => sum + item.quantity, 0)} total items`
      };

      // Try multiple API endpoints to update vehicle status
      let updateSuccess = false;
      
      try {
        // Try updating through vehicle assignment
        await axios.patch(`${API_BASE_URL}/api/vehicle-assignments/${returningVehicle.id}/`, updateData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        console.log('Vehicle assignment updated successfully');
        updateSuccess = true;
      } catch (assignmentError) {
        console.error('Failed to update via assignment endpoint:', assignmentError);
        console.error('Assignment error details:', assignmentError.response?.data);
        
        // Try updating through cars endpoint
        try {
          await axios.patch(`${API_BASE_URL}/api/cars/${returningVehicle.car}/`, updateData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          console.log('Vehicle updated via cars endpoint');
          updateSuccess = true;
        } catch (carError) {
          console.error('Failed to update via cars endpoint:', carError);
          
          // Try warehouse specific endpoint
          try {
            await axios.post(`${API_BASE_URL}/api/warehouse/create-return/`, {
              assignment_id: returningVehicle.id,
              car_id: returningVehicle.car,
              return_items: returnItems.map(item => ({
                item_name: item.item_name,
                quantity: item.quantity,
                issue_number: item.issue_number,
                source_vehicles: item.source_vehicles
              })),
              ...updateData
            }, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });
            console.log('Return created via warehouse endpoint');
            updateSuccess = true;
          } catch (warehouseError) {
            console.error('All update methods failed:', warehouseError);
          }
        }
      }

      if (updateSuccess) {
        const totalItems = returnItems.reduce((sum, item) => sum + item.quantity, 0);
        alert(`Return assignment created successfully!\n\nVehicle: ${returningVehicle.car_details?.plate_number}\nItems: ${returnItems.length} types (${totalItems} total)\nStatus: Returning to warehouse`);
        
        // Close modal and refresh data
        setShowReturnModal(false);
        setReturningVehicle(null);
        setReturnItems([]);
        fetchIncomingVehicles(); // Refresh data to show updated status
      } else {
        alert('Failed to create return assignment. Please try again or contact support.');
      }

    } catch (error) {
      console.error('Error creating return assignment:', error);
      alert('Error creating return assignment. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="w-full">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/3 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-64 bg-gray-300 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Truck className="mr-3 h-8 w-8 text-blue-600" />
                Incoming Deliveries
              </h1>
              <p className="text-gray-600 mt-2">Track vehicles coming to your warehouse with their cargo</p>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Filter Options</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Statuses</option>
                  {getUniqueStatuses().map(status => (
                    <option key={status} value={status}>
                      {statusLabels[status] || status}
                    </option>
                  ))}
                </select>
              </div>

              {/* Vehicle Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle</label>
                <input
                  type="text"
                  placeholder="Search by plate number or model..."
                  value={vehicleFilter}
                  onChange={(e) => setVehicleFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Project Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
                <input
                  type="text"
                  placeholder="Search by project name..."
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                Showing {applyFilters(incomingVehicles).length} of {incomingVehicles.length} vehicles
              </span>
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="text-2xl font-bold text-gray-900">{applyFilters(incomingVehicles).length}</div>
            <div className="text-sm text-gray-500">Filtered Deliveries</div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="text-2xl font-bold text-green-600">
              {applyFilters(incomingVehicles).filter(v => v.car_details?.car_status === 'delivered').length}
            </div>
            <div className="text-sm text-gray-500">Delivered</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="text-2xl font-bold text-orange-600">
              {applyFilters(incomingVehicles).filter(v => v.car_details?.car_status === 'not_completed').length}
            </div>
            <div className="text-sm text-gray-500">Not Completed</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">
              {applyFilters(incomingVehicles).filter(v => v.car_details?.car_status === 'in_transit_to_warehouse').length}
            </div>
            <div className="text-sm text-gray-500">Returning</div>
          </div>
        </div>

        {/* No Vehicles */}
        {Object.keys(getFilteredVehiclesByProject()).length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {Object.keys(vehiclesByProject).length === 0 ? 'No Incoming Deliveries' : 'No Results Found'}
            </h3>
            <p className="text-gray-600">
              {Object.keys(vehiclesByProject).length === 0 
                ? 'All vehicles are currently at the warehouse.' 
                : 'Try adjusting your filters to see more results.'
              }
            </p>
          </div>
        )}

        {/* Vehicles by Project */}
        {Object.entries(getFilteredVehiclesByProject()).map(([projectName, projectVehicles]) => {
          const totalProjectItems = projectVehicles.reduce((sum, assignment) => {
            const items = vehicleItems[assignment.car] || [];
            return sum + items.reduce((itemSum, item) => itemSum + (item.quantity_issued || 0), 0);
          }, 0);
          

          const isExpanded = expandedProjects[projectName] || false;

          const toggleProjectExpansion = () => {
            setExpandedProjects(prev => ({
              ...prev,
              [projectName]: !prev[projectName]
            }));
          };

          return (
            <div key={projectName} className="mb-8">
              {/* Project Header - Clickable */}
              <div 
                className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={toggleProjectExpansion}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-blue-900 flex items-center">
                      <MapPin className="w-5 h-5 mr-2" />
                      {projectName}
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 ml-2 text-blue-700" />
                      ) : (
                        <ChevronDown className="w-5 h-5 ml-2 text-blue-700" />
                      )}
                    </h2>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-700">
                      <div>
                        <span className="font-medium">Vehicles:</span> {projectVehicles.length} vehicle{projectVehicles.length !== 1 ? 's' : ''}
                      </div>
                      <div>
                        <span className="font-medium">Total Items:</span> {totalProjectItems} items
                      </div>
                    </div>
                    {/* Show project details if available */}
                    {projectVehicles[0]?.project_details && (
                      <div className="mt-2 text-xs text-blue-600">
                        <span className="font-medium">Manager:</span> {projectVehicles[0].project_details.manager || 'Not specified'} |
                        <span className="font-medium ml-2">ID:</span> {projectVehicles[0].project_details.projectNumber || projectVehicles[0].project}
                      </div>
                    )}
                  </div>
                  <div className="text-right ml-6">
                    <div className="text-3xl font-bold text-blue-900">{projectVehicles.length}</div>
                    <div className="text-sm text-blue-700 mb-3">Active</div>
                    {/* Return Items button for project */}
                    {projectVehicles.some(vehicle => 
                      vehicle.car_details?.car_status === 'delivered' || 
                      vehicle.car_details?.car_status === 'at_project_site'
                    ) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering project collapse
                          openReturnModal(projectName, projectVehicles);
                        }}
                        className="flex items-center px-3 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 transition-colors"
                      >
                        <Truck className="w-4 h-4 mr-2 transform rotate-180" />
                        Return Items
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Vehicles Table for this Project - Collapsible */}
              {isExpanded && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Departed</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cargo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {projectVehicles.map((assignment) => {
                        const items = vehicleItems[assignment.car] || [];
                        const totalItems = items.reduce((sum, item) => sum + (item.quantity_issued || item.quantity || 0), 0);

                        return (
                          <tr key={assignment.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <Truck className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {assignment.car_details?.car_brand} {assignment.car_details?.car_model}
                                  </div>
                                  <div className="text-sm text-gray-500">{assignment.car_details?.plate_number}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <User className="w-4 h-4 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {assignment.user_details?.first_name} {assignment.user_details?.last_name}
                                  </div>
                                  <div className="text-xs text-gray-500">{assignment.user_details?.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[assignment.car_details?.car_status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                                {statusLabels[assignment.car_details?.car_status] || assignment.car_details?.car_status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                                {(() => {
                                  const currentStatus = assignment.car_details?.car_status;
                                  const projectName = assignment.project_details?.name || assignment.project_name;
                                  const currentLocation = assignment.car_details?.current_location;
                                  
                                  // If going to project, show destination project name
                                  if (currentStatus === 'in_transit_to_project' && projectName) {
                                    return `→ ${projectName}`;
                                  }
                                  // If at project or returning, show project name  
                                  if ((currentStatus === 'at_project_site' || currentStatus === 'delivered' || currentStatus === 'not_completed') && projectName) {
                                    return projectName;
                                  }
                                  // If returning to warehouse
                                  if (currentStatus === 'in_transit_to_warehouse') {
                                    return '→ Warehouse';
                                  }
                                  // Default fallback
                                  return currentLocation || 'Unknown';
                                })()}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <Clock className="w-4 h-4 text-gray-400 mr-2" />
                                {new Date(assignment.assigned_date).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <Package className="w-4 h-4 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {totalItems > 0 ? `${totalItems} items` : `${items.length} items (qty 0)`}
                                  </div>
                                  <div className="text-xs text-gray-500">{items.length} types</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                {/* Show View Cargo only when needed (not at project or delivered) */}
                                {assignment.car_details?.car_status !== 'at_project_site' && 
                                 assignment.car_details?.car_status !== 'delivered' && (
                                  <button
                                    onClick={() => openItemsModal(assignment)}
                                    className="text-blue-600 hover:text-blue-900 font-medium flex items-center"
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    View Cargo
                                  </button>
                                )}
                                
                                {/* Show Confirm Delivery for vehicles at project site */}
                                {assignment.car_details?.car_status === 'at_project_site' && (
                                  <button
                                    onClick={() => openConfirmModal(assignment)}
                                    className="text-green-600 hover:text-green-900 font-medium flex items-center"
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Confirm Delivery
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              )}
            </div>
          );
        })}

        {/* Items Modal */}
        {showItemsModal && selectedVehicle && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Cargo Manifest - {selectedVehicle.car_details?.car_brand} {selectedVehicle.car_details?.car_model}
                  </h3>
                  <button
                    onClick={() => setShowItemsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>

                {/* Vehicle Info */}
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700">Vehicle:</span>
                      <p className="font-medium">{selectedVehicle.car_details?.plate_number}</p>
                    </div>
                    <div>
                      <span className="text-blue-700">Driver:</span>
                      <p className="font-medium">{selectedVehicle.user_details?.first_name} {selectedVehicle.user_details?.last_name}</p>
                    </div>
                    <div>
                      <span className="text-blue-700">From Project:</span>
                      <p className="font-medium">{selectedVehicle.project_details?.name}</p>
                    </div>
                    <div>
                      <span className="text-blue-700">Status:</span>
                      <p className="font-medium">{statusLabels[selectedVehicle.car_details?.car_status] || selectedVehicle.car_details?.car_status}</p>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-3">Items on Vehicle</h4>
                  {vehicleItems[selectedVehicle.car] && vehicleItems[selectedVehicle.car].length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue #</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {vehicleItems[selectedVehicle.car].map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">{item.item_name || 'Unknown Item'}</div>
                                  {item.item_description && (
                                    <div className="text-xs text-gray-500">{item.item_description}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.issue_number || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                {item.quantity_issued || item.quantity || 0}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                  In Transit
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No items assigned to this vehicle</p>
                    </div>
                  )}
                </div>

                {/* Summary */}
                {vehicleItems[selectedVehicle.car] && vehicleItems[selectedVehicle.car].length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Cargo Summary</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Total Items:</span>
                        <p className="font-medium text-blue-600">
                          {vehicleItems[selectedVehicle.car].reduce((sum, item) => sum + (item.quantity_issued || 0), 0)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Different Types:</span>
                        <p className="font-medium text-blue-600">{vehicleItems[selectedVehicle.car].length}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowItemsModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delivery Confirmation Modal */}
        {showConfirmModal && confirmingDelivery && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 max-w-5xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Confirm Delivery Items
                  </h3>
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Vehicle Info */}
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-900 mb-2">Vehicle Details</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700">Vehicle:</span>
                      <p className="font-medium">{confirmingDelivery.car_details?.car_brand} {confirmingDelivery.car_details?.car_model}</p>
                    </div>
                    <div>
                      <span className="text-blue-700">Plate:</span>
                      <p className="font-medium">{confirmingDelivery.car_details?.plate_number}</p>
                    </div>
                    <div>
                      <span className="text-blue-700">Driver:</span>
                      <p className="font-medium">{confirmingDelivery.user_details?.first_name} {confirmingDelivery.user_details?.last_name}</p>
                    </div>
                    <div>
                      <span className="text-blue-700">From Project:</span>
                      <p className="font-medium">{confirmingDelivery.project_details?.name || confirmingDelivery.project_name}</p>
                    </div>
                  </div>
                </div>

                {/* Confirmation Status */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">Confirmation Status</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {Object.values(confirmedItems).filter(Boolean).length} of {Object.keys(confirmedItems).length} items confirmed
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => toggleAllItems(true)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                      >
                        Confirm All
                      </button>
                      <button
                        onClick={() => toggleAllItems(false)}
                        className="px-3 py-1 bg-gray-400 text-white text-sm rounded-md hover:bg-gray-500"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Items to Confirm</h4>
                  {vehicleItems[confirmingDelivery.car] && vehicleItems[confirmingDelivery.car].length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confirm</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue #</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {vehicleItems[confirmingDelivery.car].map((item, index) => {
                            const wasConfirmed = isItemPreviouslyConfirmed(confirmingDelivery, index);
                            const currentlyConfirmed = confirmedItems[index] || false;
                            const isConfirmed = wasConfirmed || currentlyConfirmed;
                            
                            return (
                            <tr key={index} className={`hover:bg-gray-50 ${isConfirmed ? 'bg-green-50' : ''}`}>
                              <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={currentlyConfirmed}
                                  onChange={() => toggleItemConfirmation(index)}
                                  disabled={wasConfirmed}
                                  className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
                                />
                                {wasConfirmed && (
                                  <div className="text-xs text-green-600 mt-1">Previously confirmed</div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">{item.item_name || 'Unknown Item'}</div>
                                  {item.item_description && (
                                    <div className="text-xs text-gray-500">{item.item_description}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.issue_number || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                {item.quantity_issued || item.quantity || 0}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  isConfirmed 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {isConfirmed ? 'Confirmed' : 'Pending'}
                                </span>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No items assigned to this vehicle</p>
                    </div>
                  )}
                </div>

                {/* Summary */}
                {vehicleItems[confirmingDelivery.car] && vehicleItems[confirmingDelivery.car].length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">Confirmation Summary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Total Items:</span>
                        <p className="font-medium text-blue-600">{vehicleItems[confirmingDelivery.car].length}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Confirmed:</span>
                        <p className="font-medium text-green-600">{Object.values(confirmedItems).filter(Boolean).length}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Pending:</span>
                        <p className="font-medium text-orange-600">{Object.values(confirmedItems).filter(v => !v).length}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Result Status:</span>
                        <p className={`font-medium ${
                          getConfirmationStatus() === 'all' ? 'text-green-600' : 
                          getConfirmationStatus() === 'partial' ? 'text-orange-600' : 'text-gray-600'
                        }`}>
                          {getConfirmationStatus() === 'all' ? 'Delivered' : 
                           getConfirmationStatus() === 'partial' ? 'Not Completed' : 'No Changes'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
                    <p className="text-sm text-yellow-800">
                      <strong>Select each item that has been received and is in good condition.</strong>
                      The delivery status will be set automatically based on your confirmations.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => confirmDelivery(confirmingDelivery)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    disabled={getConfirmationStatus() === 'none'}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Process Delivery ({Object.values(confirmedItems).filter(Boolean).length}/{Object.keys(confirmedItems).length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Return Items Modal */}
        {showReturnModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 max-w-5xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Return Items to Warehouse
                    </h3>
                    {availableItems.length > 0 && (
                      <p className="text-sm text-blue-600 mt-1">
                        From Project: {availableItems[0]?.project_name}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowReturnModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Vehicle Selection */}
                <div className="bg-orange-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-orange-900 mb-3">Select Return Vehicle</h4>
                  {!returningVehicle ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {availableVehicles.map((vehicle) => (
                        <button
                          key={vehicle.id}
                          onClick={() => setReturningVehicle(vehicle)}
                          className="p-3 bg-white rounded-lg border border-orange-200 hover:border-orange-400 hover:bg-orange-25 text-left transition-colors"
                        >
                          <div className="font-medium text-gray-900">
                            {vehicle.car_details?.car_brand} {vehicle.car_details?.car_model}
                          </div>
                          <div className="text-sm text-gray-600">{vehicle.car_details?.plate_number}</div>
                          <div className="text-xs text-orange-600 mt-1">
                            {vehicle.project_details?.name || vehicle.project_name}
                          </div>
                        </button>
                      ))}
                      {availableVehicles.length === 0 && (
                        <div className="col-span-full text-center text-orange-700 py-4">
                          No vehicles available for return (need delivered or at project status)
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-3 border border-orange-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            {returningVehicle.car_details?.car_brand} {returningVehicle.car_details?.car_model}
                          </div>
                          <div className="text-sm text-gray-600">{returningVehicle.car_details?.plate_number}</div>
                          <div className="text-xs text-orange-600">
                            {returningVehicle.project_details?.name || returningVehicle.project_name}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-orange-700">
                            {returnItems.length} items selected
                          </div>
                          <button
                            onClick={() => {
                              setReturningVehicle(null);
                              setReturnItems([]);
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700 mt-1"
                          >
                            Change Vehicle
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Available Items */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Available Items from This Project</h4>
                    <div className="bg-gray-50 rounded-lg max-h-96 overflow-y-auto">
                      {availableItems.length > 0 ? (
                        <div className="divide-y divide-gray-200">
                          {availableItems.map((item, index) => (
                            <div key={index} className="p-3 hover:bg-gray-100">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h5 className="font-medium text-gray-900">{item.item_name || item.name}</h5>
                                  <p className="text-sm text-gray-600">
                                    Total Quantity: <span className="font-medium text-green-600">{item.total_quantity || item.quantity_issued || item.quantity || 0}</span>
                                  </p>
                                  <div className="mt-1">
                                    <p className="text-xs text-blue-600">
                                      From {item.source_vehicles ? item.source_vehicles.length : 1} vehicle(s):
                                    </p>
                                    {item.source_vehicles && item.source_vehicles.length > 0 ? (
                                      <div className="text-xs text-gray-500 mt-1">
                                        {item.source_vehicles.map((source, idx) => (
                                          <span key={idx} className="inline-block mr-2">
                                            {source.vehicle} ({source.quantity})
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-500">{item.source_vehicle}</p>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => addReturnItem(item)}
                                  disabled={!returningVehicle}
                                  className="px-3 py-1 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Add to Return
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center text-gray-500">
                          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p>No items available for return</p>
                          <p className="text-sm mt-1">Items from delivered vehicles will appear here</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Return Items List */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Items to Return</h4>
                    <div className="bg-blue-50 rounded-lg max-h-96 overflow-y-auto">
                      {returnItems.length > 0 ? (
                        <div className="divide-y divide-blue-200">
                          {returnItems.map((item, index) => (
                            <div key={index} className="p-3">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="font-medium text-blue-900">{item.item_name || item.name}</h5>
                                <button
                                  onClick={() => removeReturnItem(index)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="flex items-center space-x-2">
                                <label className="text-sm text-blue-700">Quantity:</label>
                                <input
                                  type="number"
                                  min="0"
                                  max={item.max_quantity}
                                  value={item.quantity}
                                  onChange={(e) => updateReturnItemQuantity(index, parseInt(e.target.value) || 0)}
                                  className="w-20 px-2 py-1 border border-blue-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                                <span className="text-sm text-blue-600">/ {item.max_quantity}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center text-blue-600">
                          <Truck className="w-12 h-12 text-blue-400 mx-auto mb-4 transform rotate-180" />
                          <p>No items selected for return</p>
                          <p className="text-sm mt-1">Add items from the left panel</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                {returnItems.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 mt-6">
                    <h4 className="font-medium text-gray-900 mb-2">Return Summary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Items to Return:</span>
                        <p className="font-medium text-blue-600">{returnItems.length} types</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Total Quantity:</span>
                        <p className="font-medium text-blue-600">
                          {returnItems.reduce((sum, item) => sum + item.quantity, 0)} items
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Return Vehicle:</span>
                        <p className="font-medium text-blue-600">
                          {returningVehicle ? returningVehicle.car_details?.plate_number : 'Not selected'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 my-6">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
                    <p className="text-sm text-yellow-800">
                      <strong>Selected items will be loaded onto the vehicle for return to warehouse.</strong>
                      This will create a new return assignment for the selected vehicle.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowReturnModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createReturnAssignment}
                    disabled={returnItems.length === 0 || !returningVehicle}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Truck className="w-4 h-4 mr-2 transform rotate-180" />
                    Create Return Assignment ({returnItems.reduce((sum, item) => sum + item.quantity, 0)} items)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarehouseIncomingDeliveries;